import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import {
  ensurePusher,
  disconnectPusher,
  isPusherAuthSkipped,
} from "../services/pusher"
import {
  getActivePresenceChannel,
  toPresenceMember,
} from "../constants/presence"

const PresenceContext = createContext(null)

const normalizeMember = (memberId, info = {}) => {
  const normalized = toPresenceMember({
    id: memberId,
    user_id: memberId,
    ...info,
  })
  if (normalized) return normalized
  return {
    id: String(memberId),
    name: "User",
    avatar: null,
  }
}

const membersFromPresence = members => {
  const next = {}
  if (!members) return next

  if (typeof members.each === "function") {
    members.each(member => {
      const id = String(member.id)
      next[id] = normalizeMember(id, member.info || {})
    })
    return next
  }

  Object.keys(members).forEach(id => {
    next[String(id)] = normalizeMember(id, members[id] || {})
  })
  return next
}

/**
 * Global presence provider.
 * Subscribes after login (isAuth + token + user) and tears down on logout.
 *
 * Authenticates via POST /api/v1/socket/auth once Pusher socket_id is
 * available, then joins presence-portal for member_added / member_removed.
 */
export const PresenceProvider = ({ children }) => {
  const isAuth = useSelector(state => state.auth.isAuth)
  const currentUser = useSelector(state => state.auth.currentUser)
  const token = useSelector(state => state.auth.jid)

  const [onlineUsers, setOnlineUsers] = useState({})
  const [connectionState, setConnectionState] = useState("disconnected")
  const [isSubscribed, setIsSubscribed] = useState(false)

  const channelRef = useRef(null)
  const channelNameRef = useRef(null)
  const pusherRef = useRef(null)
  const mountedRef = useRef(true)
  const currentUserRef = useRef(currentUser)
  currentUserRef.current = currentUser

  const clearPresence = useCallback(() => {
    setOnlineUsers({})
    setIsSubscribed(false)
  }, [])

  const teardownChannel = useCallback(() => {
    const pusher = pusherRef.current
    const channel = channelRef.current
    const channelName = channelNameRef.current

    if (pusher && channel && channelName) {
      try {
        channel.unbind("pusher:subscription_succeeded")
        channel.unbind("pusher:subscription_error")
        channel.unbind("pusher:member_added")
        channel.unbind("pusher:member_removed")
        pusher.unsubscribe(channelName)
      } catch {
        // ignore teardown races (Strict Mode double-invoke)
      }
    }

    channelRef.current = null
    channelNameRef.current = null
    setIsSubscribed(false)
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const shouldConnect = Boolean(isAuth && currentUser?.id && token)
    const channelName = getActivePresenceChannel()

    if (!shouldConnect) {
      teardownChannel()
      disconnectPusher()
      pusherRef.current = null
      clearPresence()
      setConnectionState("disconnected")
      return undefined
    }

    const pusher = ensurePusher({
      getAuthToken: () => token,
    })

    if (!pusher) {
      setConnectionState("failed")
      return undefined
    }

    pusherRef.current = pusher

    const handleStateChange = states => {
      if (!mountedRef.current) return
      setConnectionState(states?.current || pusher.connection.state)
    }

    setConnectionState(pusher.connection.state)
    pusher.connection.bind("state_change", handleStateChange)

    const bindPresenceHandlers = channel => {
      const onSucceeded = members => {
        if (!mountedRef.current) return

        const next = membersFromPresence(members)
        const self = toPresenceMember(currentUserRef.current)
        if (self && !next[self.id]) {
          next[self.id] = self
        }
        setOnlineUsers(next)
        setIsSubscribed(true)
      }

      const onError = status => {
        if (!mountedRef.current) return
        setIsSubscribed(false)
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[presence] subscription error on", channelName, status)
        }
      }

      const onMemberAdded = member => {
        if (!mountedRef.current) return
        const memberId = member.id || member.user_id
        if (memberId == null || memberId === "") return
        const normalized = normalizeMember(memberId, member.info || {})
        setOnlineUsers(prev => ({
          ...prev,
          [normalized.id]: normalized,
        }))
      }

      const onMemberRemoved = member => {
        if (!mountedRef.current) return
        const id = String(member.id || member.user_id || "")
        if (!id) return
        setOnlineUsers(prev => {
          if (!prev[id]) return prev
          const next = { ...prev }
          delete next[id]
          return next
        })
      }

      channel.bind("pusher:subscription_succeeded", onSucceeded)
      channel.bind("pusher:subscription_error", onError)
      channel.bind("pusher:member_added", onMemberAdded)
      channel.bind("pusher:member_removed", onMemberRemoved)
    }

    const subscribePresence = () => {
      if (!mountedRef.current) return
      if (!pusher.connection?.socket_id) return
      if (channelRef.current && channelNameRef.current === channelName) return

      const channel = pusher.subscribe(channelName)
      channelRef.current = channel
      channelNameRef.current = channelName
      bindPresenceHandlers(channel)
    }

    if (pusher.connection?.socket_id) {
      subscribePresence()
    }
    pusher.connection.bind("connected", subscribePresence)

    return () => {
      pusher.connection.unbind("connected", subscribePresence)
      pusher.connection.unbind("state_change", handleStateChange)
      teardownChannel()
      pusherRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, currentUser?.id, token, teardownChannel, clearPresence])

  const isOnline = useCallback(
    userId => {
      if (userId == null || userId === "") return false
      return Boolean(onlineUsers[String(userId)])
    },
    [onlineUsers],
  )

  const getOnlineUsers = useCallback(
    () => Object.values(onlineUsers),
    [onlineUsers],
  )

  const value = useMemo(
    () => ({
      onlineUsers,
      connectionState,
      isSubscribed,
      isOnline,
      getOnlineUsers,
      presenceChannel: getActivePresenceChannel(),
      isAuthSkipped: isPusherAuthSkipped(),
    }),
    [onlineUsers, connectionState, isSubscribed, isOnline, getOnlineUsers],
  )

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  )
}

PresenceProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const usePresenceContext = () => {
  const ctx = useContext(PresenceContext)
  if (!ctx) {
    throw new Error("usePresence must be used within a PresenceProvider")
  }
  return ctx
}

export default PresenceProvider
